<template>
  <div class="donation-container">
    <v-layout row wrap>
      <!-- Header Section -->
      <v-flex xs12>
        <v-card flat class="futuristic-card donation-header">
          <v-container fluid class="pa-4 card-header">
            <h2 class="earth-color">DONATION CAUSE MANAGEMENT</h2>
            <div class="title-decoration"></div>
          </v-container>
        </v-card>
      </v-flex>

      <!-- Form and Table Section -->
      <v-flex xs12>
        <v-card flat class="futuristic-card donation-card">
          <v-container fluid class="form-grid">
            <!-- Add/Edit Cause Form -->
            <v-flex lg4 xs12 class="pa-2">
              <div class="section-header">
                <v-icon color="#587e27">mdi-charity</v-icon>
                <span class="section-title earth-color">{{ edit ? 'Edit Donation Cause' : 'Add a Donation Cause' }}</span>
              </div>
              <v-form @submit.prevent="edit ? postEdit() : postCause()" ref="form" v-model="valid">
                <v-text-field
                  v-model="cause.name"
                  label="Cause Name"
                  :rules="requiredRules"
                  outlined
                  dense
                  class="futuristic-input"
                  prepend-inner-icon="mdi-text"
                ></v-text-field>
                <v-text-field
                  v-model="cause.gl_account_number"
                  label="GL Account Number"
                  :rules="requiredRules"
                  outlined
                  dense
                  class="futuristic-input"
                  prepend-inner-icon="mdi-numeric"
                ></v-text-field>
                <v-switch
                  v-model="cause.public"
                  label="Display to Public"
                  color="#587e27"
                  class="modern-switch"
                ></v-switch>
                <v-btn
                  depressed
                  :color="edit ? 'warning' : 'primary'"
                  block
                  @click="edit ? postEdit() : postCause()"
                  :loading="$loadingTrue"
                  class="futuristic-btn"
                >
                  <v-icon left>{{ edit ? 'mdi-pencil' : 'mdi-plus' }}</v-icon>
                  {{ edit ? 'Update Cause' : 'Add Cause' }}
                </v-btn>
                <v-btn
                  v-if="edit"
                  depressed
                  color="secondary"
                  block
                  @click="cancelCause()"
                  class="futuristic-btn mt-2"
                >
                  <v-icon left>mdi-close</v-icon>
                  Cancel Edit
                </v-btn>
              </v-form>
            </v-flex>

            <!-- Causes Table -->
            <v-flex lg8 xs12 class="pa-2">
              <div class="section-header">
                <v-icon color="#587e27">mdi-table</v-icon>
                <span class="section-title earth-color">Donation Causes</span>
              </div>
              <v-card flat class="futuristic-card table-card">
                <v-card-title>
                  <v-text-field
                    v-model="search"
                    append-icon="mdi-magnify"
                    label="Search Causes"
                    single-line
                    hide-details
                    outlined
                    dense
                    class="futuristic-input"
                  ></v-text-field>
                </v-card-title>
                <v-data-table
                  :headers="columns"
                  :items="allCauses"
                  :search="search"
                  :items-per-page-options="[10, 25, { text: 'All', value: -1 }]"
                  :disable-initial-sort="true"
                  :loading="$loadingTrue"
                  class="futuristic-table"
                >
                  <v-progress-linear slot="progress" color="primary" indeterminate></v-progress-linear>
                  <template v-slot:items="props">
                    <td>{{ props.item.name }}</td>
                    <td>{{ props.item.gl_account_number }}</td>
                    <td>{{ props.item.public === 1 ? 'Public' : '' }}</td>
                    <td>
                      <v-btn
                        depressed
                        color="warning"
                        @click="editCause(props.item)"
                        v-if="!edit"
                        class="futuristic-btn mr-2"
                      >
                        <v-icon left small>mdi-pencil</v-icon>
                        Edit
                      </v-btn>
                      <v-btn
                        depressed
                        color="error"
                        @click="deleteCause(props.item.public_id)"
                        v-if="!edit"
                        class="futuristic-btn"
                      >
                        <v-icon left small>mdi-delete</v-icon>
                        Delete
                      </v-btn>
                    </td>
                  </template>
                </v-data-table>
              </v-card>
            </v-flex>
          </v-container>
        </v-card>
      </v-flex>
    </v-layout>
  </div>
</template>

<script>
export default {
  created() {
    this.$store.dispatch('get_all_causes');
  },
  data() {
    return {
      search: '',
      edit: false,
      valid: true,
      requiredRules: [(v) => !!v || 'This field is required'],
      cause: {
        name: '',
        gl_account_number: '',
        public: false,
      },
      columns: [
        { text: 'Name', value: 'name' },
        { text: 'GL Account Number', value: 'gl_account_number' },
        { text: 'Public', value: 'public' },
        { text: 'Action', sortable: false },
      ],
    };
  },
  methods: {
    postCause() {
      if (this.$refs.form.validate()) {
        this.$store.dispatch('post_cause', this.cause);
        this.resetForm();
      }
    },
    editCause(Cause) {
      this.edit = true;
      this.cause.name = Cause.name;
      this.cause.gl_account_number = Cause.gl_account_number;
      this.cause.public = Cause.public;
    },
    cancelCause() {
      this.edit = false;
      this.resetForm();
    },
    postEdit() {
      if (this.$refs.form.validate()) {
        this.$store.dispatch('edit_cause', this.cause);
        this.edit = false;
        this.resetForm();
      }
    },
    deleteCause(publicId) {
      this.$store.dispatch('delete_cause', publicId);
    },
    resetForm() {
      this.cause.name = '';
      this.cause.gl_account_number = '';
      this.cause.public = false;
      this.$refs.form.resetValidation();
    },
  },
};
</script>

<style scoped>
/* Futuristic Card */
.futuristic-card {
  border-radius: 8px;
  background: #ffffff;
  transition: all 0.3s ease;
}

.futuristic-card:hover {
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
}

.card-header {
  background: linear-gradient(45deg, #e8eaf6, #f5f7fa);
  padding: 16px;
}

.earth-color {
  color: #587e27;
  font-weight: 600;
  margin: 0;
}

.futuristic-search {
  max-width: 250px;
}

.futuristic-search >>> .v-input__slot {
  border-radius: 8px;
  background: #f5f7fa;
}

.futuristic-input >>> .v-input__slot {
  border-radius: 8px;
  background: #f5f7fa;
}

.futuristic-btn {
  border-radius: 8px;
  text-transform: none;
  font-weight: 500;
  transition: all 0.2s ease;
}

.futuristic-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.futuristic-table {
  border-radius: 8px;
  margin: 0 8px 8px;
}

.futuristic-table >>> table {
  border-collapse: separate;
  border-spacing: 0;
}

.futuristic-table >>> thead tr th {
  background: linear-gradient(45deg, #e8eaf6, #f5f7fa);
  color: #587e27;
  font-weight: 600;
  font-size: 13px;
}

.futuristic-table >>> tbody tr {
  transition: background-color 0.2s ease;
}

.futuristic-table >>> tbody tr:hover {
  background-color: #f5f7fa;
}

.futuristic-link {
  color: #1976d2;
  text-decoration: none;
  font-weight: 500;
}

.futuristic-link:hover {
  color: #0d47a1;
}

.futuristic-alert {
  border-radius: 8px;
  margin: 8px;
}

/* Retain original classes for layout */
.donation-header, .donation-card, .table-card {
  background: transparent !important; /* Ensure futuristic-card styles take precedence */
}

.form-grid {
  display: flex;
  flex-wrap: wrap;
}

/* Retain title-decoration for existing styling */
.title-decoration {
  height: 4px;
  background: linear-gradient(90deg, #587e27, transparent);
  margin-top: 8px;
  width: 100px;
}

/* Section header styling */
.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
}

.section-title {
  margin-left: 8px;
  font-size: 1.2rem;
  font-weight: 500;
}

/* Modern switch styling */
.modern-switch {
  margin-top: 8px;
}

/* Responsive adjustments */
@media (max-width: 960px) {
  .card-header {
    flex-direction: column;
    align-items: flex-start;
    padding: 12px;
  }

  .futuristic-search {
    max-width: 100%;
    margin-top: 12px;
  }

  .futuristic-table {
    margin: 0 4px 4px;
  }

  .futuristic-table >>> thead tr th {
    font-size: 12px;
    padding: 0 8px;
  }

  .futuristic-table >>> tbody tr td {
    font-size: 12px;
    padding: 0 8px;
  }

  .futuristic-btn {
    font-size: 0.9rem;
  }
}

@media (max-width: 600px) {
  .futuristic-card {
    border-radius: 6px;
  }

  .futuristic-btn {
    font-size: 0.85rem;
  }

  .futuristic-table {
    margin: 0 4px 4px;
    overflow-x: auto;
  }

  .futuristic-table >>> table {
    min-width: 700px; /* Ensure table is wide enough for all columns */
  }

  .futuristic-table >>> thead {
    display: table-header-group;
  }

  .futuristic-table >>> tbody tr {
    display: table-row;
  }

  .futuristic-table >>> tbody tr td {
    display: table-cell;
    font-size: 0.85rem;
    padding: 6px 8px;
  }

  .futuristic-table >>> thead tr th {
    font-size: 0.85rem;
    padding: 6px 8px;
  }
}
</style>
